import { useEffect, useState } from 'react';

import { AlertTriangle, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';

import { CartaoDeConfiguracao } from './CartaoDeConfiguracao';
import { CHAVES, useConfiguracao, type DesvioDeAlerta } from './dados';

const PCT_PADRAO = 15;

function limitar(valor: number): number {
  if (!Number.isFinite(valor)) {
    return PCT_PADRAO;
  }
  return Math.max(1, Math.min(100, Math.trunc(valor)));
}

/**
 * `/admin/configuracoes/desvio-alerta` — o percentual de variação
 * contra o mês anterior a partir do qual a aprovação de lote sinaliza desvio.
 *
 * O alerta é visual e não bloqueia nada: não impede a aprovação nem exige
 * justificativa. Serve para quem aprova olhar duas vezes antes de assinar um
 * lote que fugiu do padrão.
 *
 * "Salvar" não grava — ver `CartaoDeConfiguracao.tsx`.
 */
export function DesvioAlertaPage() {
  const consulta = useConfiguracao<DesvioDeAlerta>(CHAVES.desvioAlerta);

  const [percentual, setPercentual] = useState(PCT_PADRAO);

  useEffect(() => {
    if (consulta.data) {
      setPercentual(limitar(consulta.data.pct));
    }
  }, [consulta.data]);

  function validar(): boolean {
    if (!Number.isInteger(percentual) || percentual < 1 || percentual > 100) {
      toast.error('Percentual inválido', { description: 'Informe um percentual entre 1 e 100.' });
      return false;
    }
    return true;
  }

  return (
    <CartaoDeConfiguracao
      titulo="Alerta de desvio do lote"
      descricao="Define quanto o lote pode variar contra o mês anterior antes de o sistema sinalizar desvio anormal na aprovação."
      tituloDoCartao="Limite de variação"
      icone={AlertTriangle}
      carregando={consulta.isLoading}
      comErro={consulta.isError}
      mensagemDeErro="Não foi possível carregar o limite de desvio agora. Verifique sua conexão e tente novamente."
      aoTentarDeNovo={() => void consulta.refetch()}
      aoValidar={validar}
      acaoSecundaria={
        <Button variant="outline" size="sm" onClick={() => setPercentual(PCT_PADRAO)}>
          <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden="true" />
          Restaurar padrão ({PCT_PADRAO}%)
        </Button>
      }
    >
      <div className="space-y-2">
        <Label htmlFor="desvio-pct">Percentual</Label>
        <div className="flex items-center gap-2">
          <Input
            id="desvio-pct"
            type="number"
            min={1}
            max={100}
            value={percentual}
            onChange={(evento) => setPercentual(limitar(Number(evento.target.value)))}
            className="w-32"
          />
          <span className="text-sm text-muted-foreground">%</span>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Com {percentual}%, se o valor total do lote — ou a quantidade de medições — variar mais que
        isso para cima ou para baixo em relação ao mês anterior, o diálogo de aprovação mostra um
        aviso. O alerta não bloqueia a aprovação nem exige justificativa.
      </p>
    </CartaoDeConfiguracao>
  );
}

export default DesvioAlertaPage;
